import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton } from '../../naked';
import { IconButton } from '../icon-button';
import { useSlideCarousel } from './provider';

type SlideNavigationProps = {
  variant?: 'default' | 'fullIndicators' | 'centeredIndicators';
  activeCenteredIndicatorColor?: string;
  trackingId?: string;
};

export const SlideNavigation = ({
  variant = 'default',
  activeCenteredIndicatorColor,
  trackingId = 'navigate-slide',
}: SlideNavigationProps) => {
  const { activeId, setActiveId, slideIds } = useSlideCarousel();
  const activeIndex = slideIds.indexOf(activeId);

  const handlePrev = () => {
    const prev = slideIds[activeIndex - 1];
    if (activeIndex > 0 && prev) {
      setActiveId(prev);
    }
  };

  const handleNext = () => {
    const next = slideIds[activeIndex + 1];
    if (activeIndex < slideIds.length - 1 && next) {
      setActiveId(next);
    }
  };

  const isFullIndicatorsVariant = variant === 'fullIndicators';
  const isCenteredIndicators = variant === 'centeredIndicators';

  return (
    <footer css={buttonContainerStyle}>
      {!isFullIndicatorsVariant && !isCenteredIndicators && (
        <div css={css({ display: 'flex', gap: theme.spacing(1) })}>
          <IconButton
            css={buttonStyle}
            size='xsmall'
            label='Next'
            disabled={activeIndex === 0}
            onClick={handlePrev}
            trackingId={`${trackingId}-prev`}
          >
            <Icon name='caret-left' />
          </IconButton>
          <IconButton
            css={buttonStyle}
            size='xsmall'
            label='Forward'
            disabled={activeIndex === slideIds.length - 1}
            onClick={handleNext}
            trackingId={`${trackingId}-next`}
          >
            <Icon name='caret-right' />
          </IconButton>
        </div>
      )}
      <div css={[css({ display: 'flex', gap: theme.spacing(1) }), isFullIndicatorsVariant && css({ width: '100%' })]}>
        {slideIds.map((id, index) => (
          <ActiveSectionIndicator
            variant={variant}
            key={id}
            isActive={activeIndex === index}
            onClick={() => setActiveId(id)}
            activeCenteredIndicatorColor={activeCenteredIndicatorColor}
          />
        ))}
      </div>
    </footer>
  );
};

const buttonStyle = css({
  padding: theme.spacing(0.5),
});

interface ActiveSectionIndicatorProps {
  isActive: boolean;
  onClick: () => void;
  variant?: SlideNavigationProps['variant'];
  activeCenteredIndicatorColor?: string;
}

const ActiveSectionIndicator = ({
  isActive,
  onClick,
  variant,
  activeCenteredIndicatorColor,
}: ActiveSectionIndicatorProps) => {
  const isFullIndicatorsVariant = variant === 'fullIndicators';
  const isCenteredIndicators = variant === 'centeredIndicators';

  return (
    <NakedButton
      onClick={onClick}
      css={[
        css`
          display: block;
          height: 8px;
          width: 8px;
          border-radius: ${theme.borderRadius.full};
          transition: background-color 0.3s ease;
          background-color: ${isActive ? theme.colors.neutral90 : theme.colors.neutral20};
          cursor: pointer;
        `,
        isFullIndicatorsVariant &&
          css`
            width: 100%;
            background-color: ${isActive ? theme.colors.primary50 : theme.colors.neutral10};
          `,
        isCenteredIndicators &&
          activeCenteredIndicatorColor &&
          css`
            background-color: ${isActive ? activeCenteredIndicatorColor : theme.colors.white};
          `,
      ]}
    />
  );
};

const buttonContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  alignItems: 'baseline',
});
